import type { default as Candidate } from 'model/Candidate';
import { extractLocation } from 'shared/legacy/contexts/CandidateContext/searchv3/extractLocation';
import { useCampaignFiltersStore } from 'stores/campaign/campaign-filters.store';

const replaceFullNameWithFirstName = (text: string, fullName: string, firstName: string): string => {
  if (!fullName || fullName === firstName) return text;
  const escaped = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(escaped, 'gi'), firstName);
};

export const summarizeCandidateForAI = (candidate: Candidate): string => {
  const summarized: { [key: string]: any } = {};
  const firstName = candidate.firstName ?? candidate.name?.split(' ')[0] ?? '';

  summarized.name = firstName;
  summarized.job_title = candidate.headline;
  summarized.about = candidate.about;
  summarized.experiences = candidate.experiences
    .slice(0, 3)
    .map((exp) => {
      const role = exp.role ?? exp.description ?? 'Role not specified';
      const company = exp.company?.name ?? 'Company not specified';

      const startMonth = exp.dates?.start?.month?.toString().padStart(2, '0');
      const startYear = exp.dates?.start?.year;
      const endMonth = exp.dates?.end?.month?.toString().padStart(2, '0');
      const endYear = exp.dates?.end?.year;

      const period =
        startMonth && startYear && endMonth && endYear
          ? ` (${startMonth}/${startYear} - ${endMonth}/${endYear})`
          : startMonth && startYear
            ? ` (${startMonth}/${startYear} - Current)`
            : '';

      return `${role} at ${company}${period}`;
    })
    .join('; ');
  summarized.education = candidate.education
    .slice(0, 3)
    .map((education) => `${education.name} - (${education.endYear})`)
    .join('; ');
  summarized.courses = candidate.courses?.map((course) => course.name).join('; ');
  summarized.skills = candidate.skills.map((skill) => skill.name).join('; ');
  summarized.certifications = candidate.certifications?.map((certification) => certification.name).join('; ');
  summarized.licenses = candidate.licenses?.map((license) => license.name).join('; ');
  summarized.preferences = candidate.preferences
    ?.map((preference) => `${preference.title}: ${preference.description}`)
    .join('; ');
  summarized.volunteeringExperiences = candidate.volunteeringExperiences?.map((volunteer) => volunteer.name).join('; ');
  summarized.location = candidate.location?.name;
  summarized.distanceBetweenCandidateAndJobLocation = extractLocation(
    candidate,
    useCampaignFiltersStore.getState().locations,
  )?.distance;

  const cleaned = Object.fromEntries(Object.entries(summarized).filter(([, value]) => Boolean(value)));
  const json = JSON.stringify(cleaned, null, 2);

  return replaceFullNameWithFirstName(json, candidate.name, firstName);
};
